import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LogIn, Mail, Lock, Building2, Eye, EyeOff, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ThemeToggle } from "@/components/ThemeToggle";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [keepConnected, setKeepConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const from = (location.state as any)?.from || "/dashboard";
  const wasRedirected = !!(location.state as any)?.from;

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast({ title: "Erro", description: "Preencha todos os campos.", variant: "destructive" });
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);

    if (error) {
      toast({ title: "Erro ao entrar", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Sucesso", description: "Login realizado com sucesso!" });
    navigate(from, { replace: true });
  };

  return (
    <div className="flex min-h-screen">
      {/* Left panel - branding */}
      <div className="hidden lg:flex lg:w-[45%] bg-primary relative overflow-hidden items-center justify-center p-12">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-primary/80" />
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-primary-foreground/5 -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-72 h-72 rounded-full bg-primary-foreground/5 translate-y-1/2 -translate-x-1/2" />
        <div className="relative z-10 text-primary-foreground space-y-6 max-w-md animate-fade-in">
          <div className="w-14 h-14 rounded-2xl bg-primary-foreground/15 backdrop-blur flex items-center justify-center">
            <Building2 className="h-7 w-7" />
          </div>
          <h2 className="text-3xl font-bold leading-tight">
            Gerencie suas clínicas com eficiência e segurança.
          </h2>
          <p className="text-primary-foreground/70 text-base leading-relaxed">
            O Busca Clínicas oferece ferramentas completas para administrar clínicas, internações, leads e orçamentos em um só lugar.
          </p>
          <div className="flex gap-8 pt-4">
            <div>
              <p className="text-2xl font-bold">500+</p>
              <p className="text-sm text-primary-foreground/60">Clínicas</p>
            </div>
            <div>
              <p className="text-2xl font-bold">1.2k</p>
              <p className="text-sm text-primary-foreground/60">Usuários</p>
            </div>
            <div>
              <p className="text-2xl font-bold">99.9%</p>
              <p className="text-sm text-primary-foreground/60">Uptime</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-8 bg-background relative">
        <div className="absolute top-4 right-4">
          <ThemeToggle />
        </div>

        <div className="w-full max-w-[420px] space-y-8 animate-fade-in">
          <div className="lg:hidden text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary text-primary-foreground mb-3">
              <Building2 className="h-6 w-6" />
            </div>
            <h1 className="text-xl font-bold text-primary">Busca Clínicas</h1>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-foreground">Bem-vindo de volta!</h2>
            <p className="text-muted-foreground text-sm mt-1">Faça login para acessar o painel.</p>
          </div>

          {wasRedirected && (
            <Alert variant="destructive" className="border-destructive/30 bg-destructive/5">
              <ShieldAlert className="h-4 w-4" />
              <AlertDescription className="text-sm">
                Sua sessão expirou ou você precisa fazer login para acessar essa página.
              </AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleLogin} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">E-mail</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  className="pl-10 h-11"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium">Senha</Label>
                <a href="/forgot-password" className="text-xs text-primary hover:underline font-medium">
                  Esqueci minha senha
                </a>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  className="pl-10 pr-10 h-11"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="keep-connected"
                checked={keepConnected}
                onCheckedChange={(v) => setKeepConnected(v === true)}
              />
              <label htmlFor="keep-connected" className="text-sm text-muted-foreground cursor-pointer select-none">
                Manter conectado
              </label>
            </div>

            <Button
              type="submit"
              size="lg"
              disabled={loading}
              className="w-full h-11 text-sm font-semibold gap-2 shadow-sm"
            >
              <LogIn className="h-4 w-4" />
              {loading ? "Entrando..." : "Entrar"}
            </Button>
          </form>

          <div className="text-center">
            <span className="text-sm text-muted-foreground">É um afiliado? </span>
            <a href="/registro" className="text-sm text-primary font-semibold hover:underline">
              Cadastre-se aqui
            </a>
          </div>

          <p className="text-xs text-muted-foreground text-center">
            Busca Clínicas &copy; 2026
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
